import { CheckBox, Modal, Input, InputDropdown, InputDropdownItem } from '@platform/components';
import { AdminProjects } from '@platform/client';
import { AdminProject, ReviewProject } from '@platform/client/schemas';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectPublishingStage } from '@platform/client/schemas';
import { snakeToTitleCase } from '../../../util/TitleCase';

type ProjectPublishStageModalProps = {
  onClose: () => void;
  project: AdminProject | ReviewProject;
};

const ProjectPublishStageModal = (props: ProjectPublishStageModalProps) => {
  const { onClose, project } = props;
  const [publishStage, setPublishStage] = useState<ProjectPublishingStage>();
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [revisionNotes, setRevisionNotes] = useState<string>('');

  const onModalClose = () => {
    setPublishStage(undefined);
    setSendEmail(false);
    setRevisionNotes('');
    onClose();
  };

  const items: InputDropdownItem[] = Object.values(ProjectPublishingStage).map((stage) => ({
    text: snakeToTitleCase(stage),
    textValue: stage,
  }));

  const queryClient = useQueryClient();

  const updatePublishStage = AdminProjects.useUpdatePublishStage({
    mutation: {
      onMutate: () => {
        onModalClose();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...AdminProjects.getGetProjectsQueryKey({})],
        });
        queryClient.invalidateQueries({
          queryKey: [...AdminProjects.getGetReviewProjectQueryKey(project.id)],
        });
      },
    },
  });

  return (
    <Modal
      headerDivision='divider'
      variant='dialog'
      title={`Update Project (${project?.id}) Publish Stage`}
      isOpen={true}
      onClose={onModalClose}
      primaryCTA={{
        text: 'Update Stage',
        callback: () => {
          if (publishStage) {
            updatePublishStage.mutate({
              data: {
                projectId: project.id,
                publishStage,
                sendEmail,
                revisionNotes,
              },
            });
          }
          onModalClose();
        },
        isDisabled: () =>
          !publishStage || (publishStage === 'revision_requested' && sendEmail && !revisionNotes),
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: onModalClose,
      }}
      ignoreExtraPadding
    >
      <div className='flex flex-col gap-4 w-[450px]'>
        <div className='flex flex-col gap-2'>
          <span className='text-base font-semibold'>Project</span>
          <span className='text-base text-gray-500'>{project?.name}</span>
          <span className='text-base font-semibold'>Current Stage</span>
          <span className='text-base text-gray-500'>
            {project?.publishingStage.replaceAll('_', ' ')}
          </span>
        </div>
        <div className='flex flex-col gap-2'>
          <span className='text-base font-semibold'>New Stage</span>
          <InputDropdown
            aria-label='project publish stage'
            type='default'
            items={items}
            inputDisabled
            onSelectionChange={(key) => setPublishStage(key as ProjectPublishingStage)}
          ></InputDropdown>
        </div>
        {publishStage && publishStage !== 'draft' && publishStage !== 'unpublished' && (
          <CheckBox
            aria-label='send email'
            description='Notify the project representative'
            size='md'
            onChange={(value) => {
              setSendEmail(value);
            }}
          >
            Send Email
          </CheckBox>
        )}
        {sendEmail && publishStage === 'revision_requested' && (
          <Input
            type='multiline'
            label='Email Note'
            value={revisionNotes}
            placeholder='Add personal note here'
            onChange={(e) => setRevisionNotes(e.target.value)}
            required={true}
            error={!revisionNotes ? 'note required if sending email for revision' : undefined}
          />
        )}
      </div>
    </Modal>
  );
};

export default ProjectPublishStageModal;
